import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    verifyPayment(sessionId);
  }, [searchParams]);

  const verifyPayment = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);

        if (response.data.payment_status === 'paid') {
          setPaymentData(response.data);
          setStatus('success');
          return true;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          return true;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 2000);
        } else {
          setStatus('error');
        }

        return false;
      } catch (error) {
        setStatus('error');
        return true;
      }
    };

    checkStatus();
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <h2 className="text-xl font-bold mb-2">Verifying Payment...</h2>
            <p className="text-muted-foreground text-center">
              Please wait while we confirm your payment
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30" data-testid="payment-success-page">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-12">
            <CheckCircle2 className="h-16 w-16 text-green-600 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground text-center mb-6">
              Thank you for your purchase. You'll receive an email confirmation shortly.
            </p>
            {paymentData && (
              <div className="w-full border-t pt-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Amount Paid:</span>
                  <span className="font-medium">${(paymentData.amount_total / 100).toFixed(2)}</span>
                </div>
              </div>
            )}
            <Button className="w-full mt-6" onClick={() => navigate('/')} data-testid="back-home-btn">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center py-12">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <span className="text-red-600 text-3xl">×</span>
          </div>
          <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
          <p className="text-muted-foreground text-center mb-6">
            There was an issue processing your payment. Please try again.
          </p>
          <Button className="w-full" onClick={() => navigate(-1)} data-testid="retry-btn">
            Try Again
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
